import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Filter, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import SchoolCard from "@/components/SchoolCard";

const TYPES = ["Private", "Public"];
const LEVELS = ["Nursery", "Primary", "Secondary", "Sixth Form"];
const FEE_BUCKETS = [
  { label: "Under ₦500k", min: null, max: 500_000 },
  { label: "₦500k – ₦1M", min: 500_000, max: 1_000_000 },
  { label: "₦1M – ₦2.5M", min: 1_000_000, max: 2_500_000 },
  { label: "₦2.5M – ₦5M", min: 2_500_000, max: 5_000_000 },
  { label: "₦5M+", min: 5_000_000, max: null },
];

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [schools, setSchools] = useState([]);
  const [total, setTotal] = useState(0);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const q = params.get("q") || "";
  const district = params.get("district") || "";
  const type = params.get("type") || "";
  const level = params.get("level") || "";
  const feeBucket = params.get("fee") || "";

  const [localQ, setLocalQ] = useState(q);
  useEffect(() => setLocalQ(q), [q]);

  const activeFees = useMemo(
    () => FEE_BUCKETS.find((f) => f.label === feeBucket) || null,
    [feeBucket]
  );

  const fetchSchools = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams();
    if (q) query.set("q", q);
    if (district) query.set("district", district);
    if (type) query.set("type", type);
    if (level) query.set("level", level);
    if (activeFees?.min != null) query.set("fee_min", activeFees.min);
    if (activeFees?.max != null) query.set("fee_max", activeFees.max);
    query.set("limit", "60");
    try {
      const { data } = await api.get(`/schools?${query.toString()}`);
      setSchools(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setSchools([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, district, type, level, activeFees]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  useEffect(() => {
    api.get("/districts").then((r) => setDistricts(r.data.districts || [])).catch(() => {});
  }, []);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const clearAll = () => {
    setParams(new URLSearchParams());
  };

  const onSearch = (e) => {
    e.preventDefault();
    updateParam("q", localQ.trim());
  };

  const activeFiltersCount =
    (district ? 1 : 0) + (type ? 1 : 0) + (level ? 1 : 0) + (feeBucket ? 1 : 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      {/* Search bar */}
      <div className="mb-6">
        <h1
          className="font-display text-3xl md:text-4xl font-bold text-stone-900"
          data-testid="browse-title"
        >
          Browse schools
        </h1>
        <p className="text-stone-600 mt-1">
          {loading ? "Loading…" : `${total} school${total === 1 ? "" : "s"} found`}
        </p>
        <form
          onSubmit={onSearch}
          className="mt-5 bg-white rounded-xl border border-stone-200 p-1.5 flex items-center gap-2 max-w-2xl"
        >
          <div className="flex items-center flex-1 pl-2">
            <Search className="w-5 h-5 text-stone-400 shrink-0" />
            <input
              value={localQ}
              onChange={(e) => setLocalQ(e.target.value)}
              type="text"
              placeholder="Search by name, district…"
              data-testid="browse-search-input"
              className="flex-1 px-3 py-2.5 bg-transparent focus:outline-none"
            />
          </div>
          <button
            type="submit"
            data-testid="browse-search-submit"
            className="bg-[#C85A32] hover:bg-[#A84624] text-white font-semibold px-4 py-2.5 rounded-lg transition-colors"
          >
            Search
          </button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        {/* Filters */}
        <aside>
          <div className="flex items-center justify-between lg:hidden mb-3">
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="toggle-filters-mobile"
              className="inline-flex items-center gap-2 text-sm font-semibold text-stone-900 bg-white border border-stone-200 rounded-lg px-4 py-2.5"
            >
              <Filter className="w-4 h-4" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="ml-1 bg-[#C85A32] text-white text-xs rounded-full w-5 h-5 inline-flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            {activeFiltersCount > 0 && (
              <button
                onClick={clearAll}
                data-testid="clear-filters-mobile"
                className="text-sm text-[#C85A32] font-medium"
              >
                Clear all
              </button>
            )}
          </div>

          <div
            className={`${
              filtersOpen ? "block" : "hidden"
            } lg:block bg-white rounded-2xl border border-stone-200 p-5 lg:sticky lg:top-20`}
            data-testid="filter-sidebar"
          >
            <div className="hidden lg:flex items-center justify-between mb-5">
              <h2 className="font-display font-bold text-stone-900">Filters</h2>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearAll}
                  data-testid="clear-filters"
                  className="text-xs text-[#C85A32] font-semibold inline-flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>

            <FilterGroup label="District">
              <select
                value={district}
                onChange={(e) => updateParam("district", e.target.value)}
                data-testid="filter-district"
                className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C85A32]"
              >
                <option value="">All districts</option>
                {districts.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </FilterGroup>

            <FilterGroup label="Type">
              <div className="flex flex-wrap gap-2">
                {TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`filter-type-${t.toLowerCase()}`}
                    onClick={() => updateParam("type", type === t ? "" : t)}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors font-medium ${
                      type === t
                        ? "bg-[#1E3A2B] text-white border-[#1E3A2B]"
                        : "bg-white text-stone-700 border-stone-200 hover:border-[#1E3A2B]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </FilterGroup>

            <FilterGroup label="Level">
              <div className="flex flex-wrap gap-2">
                {LEVELS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    data-testid={`filter-level-${l.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => updateParam("level", level === l ? "" : l)}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors font-medium ${
                      level === l
                        ? "bg-[#C85A32] text-white border-[#C85A32]"
                        : "bg-white text-stone-700 border-stone-200 hover:border-[#C85A32]"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </FilterGroup>

            <FilterGroup label="Fees per year">
              <div className="flex flex-col gap-1.5">
                {FEE_BUCKETS.map((f) => (
                  <label
                    key={f.label}
                    data-testid={`filter-fee-${f.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                      feeBucket === f.label
                        ? "bg-amber-50 border-amber-300 text-amber-900"
                        : "bg-white border-stone-200 text-stone-700 hover:border-stone-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="fee"
                      className="sr-only"
                      checked={feeBucket === f.label}
                      onChange={() =>
                        updateParam("fee", feeBucket === f.label ? "" : f.label)
                      }
                    />
                    <span className="font-medium">{f.label}</span>
                  </label>
                ))}
              </div>
            </FilterGroup>
          </div>
        </aside>

        {/* Results */}
        <div data-testid="browse-results-grid">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
                >
                  <div className="aspect-[16/10] bg-stone-100 animate-pulse" />
                  <div className="p-5 space-y-3">
                    <div className="h-5 bg-stone-100 rounded animate-pulse" />
                    <div className="h-4 bg-stone-100 rounded w-2/3 animate-pulse" />
                    <div className="h-4 bg-stone-100 rounded w-1/2 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : schools.length === 0 ? (
            <div
              data-testid="browse-empty"
              className="bg-white rounded-2xl border border-stone-200 p-10 text-center"
            >
              <div className="font-display text-xl font-semibold text-stone-900">
                No schools match these filters
              </div>
              <p className="text-stone-500 mt-2">
                Try widening your search or clearing filters.
              </p>
              <button
                onClick={clearAll}
                className="mt-5 bg-[#C85A32] text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-[#A84624]"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {schools.map((s) => (
                <SchoolCard key={s.id} school={s} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="mb-5 last:mb-0">
      <div className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}
